/**
 * CITAS MEDICAS — la tarjeta, el formulario y el bloque de la ficha.
 *
 * Punto 10, fase 2 (Lissette, 2026-09-15/16). Vive aparte de `MedicosTab.jsx`
 * porque lo usan DOS pantallas: el bloque dentro de la ficha del cliente y la
 * lista global de Tareas → Citas médicas (`CitasMedicasView.jsx`). Dos copias
 * de la tarjeta acabarian enseñando cosas distintas en cada sitio.
 *
 * ── LO QUE DECIDE COMO SE VE ────────────────────────────────────────────────
 *
 * En la vista previa de Apizeal el campo que de verdad se lee el dia de la cita
 * es la RAZON: dice que chequeo es, que medico atiende, de que numero se saco la
 * cita, quien hizo de interprete y cuantos minutos antes hay que llegar. Por eso
 * aqui va entera y sin recortar, no detras de un ℹ️ como alli.
 *
 * El PROVEEDOR sale del medico de la ficha cuando la cita cuelga de uno —asi un
 * telefono corregido una vez vale para todas sus citas— y solo se teclea cuando
 * no cuelga de ninguno. Si el medico se borro de la ficha, la cita enseña la
 * copia que guardo Y LO DICE, en vez de quedarse sin proveedor.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Input, Textarea, Select, confirmDialog } from './ui.jsx';
import { toast } from './Toast.jsx';
import { AddressSuggestions } from './AddressSuggestions.jsx';
import { fmt } from '../utils.js';
import {
  ESTATUS_DE_CITA, ESTATUS, etiquetaDeEstatus, colorDeEstatus, ESTATUS_CERRADOS,
  CAMPOS_DE_CITA, LARGO_MAXIMO, ETIQUETAS,
  citaVacia, citaComoFormulario, cuandoEs, cuantoFalta,
  DIAS_SUGERIDOS, MAXIMO_DE_AVISOS, DIAS_MAXIMO, normalizarDias, etiquetaDeAviso, textoDeLaConfirmacion,
} from '../citasMedicas.js';
import {
  listMedicalAppointments, createMedicalAppointment, updateMedicalAppointment,
  setMedicalAppointmentStatus, deleteMedicalAppointment,
} from '../api.js';

const VALOR_SUELTO = '__suelto__';

/** El chip del estatus, que además es el desplegable para cambiarlo. */
export function ChipDeEstatus({ cita, lang = 'es', onCambiar, disabled }) {
  const es = lang !== 'en';
  const color = colorDeEstatus(cita.status);
  const meta = ESTATUS[cita.status] || ESTATUS.pendiente;
  if (!onCambiar) {
    return (
      <span style={{ fontSize: '.68rem', fontWeight: 700, padding: '.2rem .55rem', borderRadius: 100,
        background: `${color}1a`, border: `1px solid ${color}55`, color }}>
        {meta.icono} {etiquetaDeEstatus(cita.status, lang)}
      </span>
    );
  }
  return (
    <select
      value={cita.status} disabled={disabled}
      onChange={(e) => onCambiar(e.target.value)}
      title={es ? 'Cambiar el estatus' : 'Change the status'}
      style={{ fontSize: '.68rem', fontWeight: 700, padding: '.18rem .4rem', borderRadius: 100,
        background: `${color}1a`, border: `1px solid ${color}55`, color,
        cursor: disabled ? 'wait' : 'pointer', fontFamily: 'inherit', outline: 'none' }}>
      {ESTATUS_DE_CITA.map((id) => (
        <option key={id} value={id}>{ESTATUS[id].icono} {etiquetaDeEstatus(id, lang)}</option>
      ))}
    </select>
  );
}

/** Una cita, como se ve en la ficha y en la lista global. */
export function TarjetaDeCita({ cita, lang = 'es', onEditar, onBorrar, onEstatus, cambiando, nombreDePersona, mostrarCliente }) {
  const es = lang !== 'en';
  const cerrada = ESTATUS_CERRADOS.includes(cita.status);
  const p = cita.proveedor || {};
  const resumenProveedor = [p.especialidad, p.npi ? `NPI ${p.npi}` : ''].filter(Boolean).join(' · ');
  return (
    <div style={{ padding: '.7rem .85rem', borderTop: '1px solid rgba(26,53,80,.45)', opacity: cerrada ? 0.72 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '.6rem', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--c-white,#eef6ff)' }}>
              📅 {cuandoEs(cita.startsAt, lang)}
            </span>
            {/* "En 4 días" / "Hace 2 días": es lo primero que se mira en una
                lista de citas, y ahorra restar fechas de cabeza. */}
            <span style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>
              {cuantoFalta(cita.startsAt, lang)}
            </span>
            <ChipDeEstatus cita={cita} lang={lang} onCambiar={onEstatus} disabled={cambiando} />
            {cita.enElCalendario && (
              <span title={es ? 'También está en el calendario del agente' : 'Also on the agent calendar'}
                style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>🗓️</span>
            )}
            {/* Que el cliente vaya a recibir un correo tiene que VERSE en la
                tarjeta, sin abrir la cita: es lo único que sale del CRM. */}
            {!!(cita.reminderDays || []).length && (
              <span title={(cita.reminderDays || []).map((d) => {
                const cuando = (cita.recordatoriosEnviados || {})[String(d)];
                return `${etiquetaDeAviso(d, lang)}${cuando ? (es ? ' · ya enviado' : ' · already sent') : ''}`;
              }).join(' · ')}
                style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>
                📧 {(cita.reminderDays || []).map((d) => `${d}d`).join('/')}
                {Object.keys(cita.recordatoriosEnviados || {}).length ? ' ✓' : ''}
              </span>
            )}
          </div>

          {mostrarCliente && !!cita.clientName && (
            <div style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--c-cyan,#00d4ff)', marginTop: 3 }}>
              👤 {cita.clientName}
              {!!nombreDePersona && nombreDePersona !== cita.clientName && (
                <span style={{ color: 'var(--c-text3,#45688a)', fontWeight: 400 }}> · {nombreDePersona}</span>
              )}
            </div>
          )}
          {!mostrarCliente && !!nombreDePersona && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginTop: 2 }}>👤 {nombreDePersona}</div>
          )}

          {!!p.nombre && (
            <div style={{ fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', marginTop: 3 }}>
              🩺 {p.nombre}
              {!!resumenProveedor && <span style={{ color: 'var(--c-text2,#7aa3c0)' }}> · {resumenProveedor}</span>}
              {!!p.telefono && <span style={{ color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono)' }}> · ☎ {p.telefono}</span>}
            </div>
          )}
          {/* El medico al que colgaba esta cita ya no esta en la ficha. Se
              enseña la copia que se guardo, y se DICE: sin el aviso alguien
              corregiria el telefono del medico y esta cita no cambiaria. */}
          {p.origen === 'copia' && (
            <div style={{ fontSize: '.68rem', color: 'var(--c-amber,#ffb700)', marginTop: 2 }}>
              ⚠️ {es
                ? 'Este médico ya no está en la ficha; se muestra lo que se guardó con la cita.'
                : 'This doctor is no longer on the record; showing what was saved with the appointment.'}
            </div>
          )}
          {!p.nombre && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginTop: 3 }}>
              🩺 {es ? 'Sin proveedor puesto' : 'No provider on file'}
            </div>
          )}

          {!!cita.orden && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
              # {cita.orden}
            </div>
          )}
          {!!cita.direccion && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2 }}>
              📍 {cita.direccion}{cita.condado ? ` · ${es ? 'Condado' : 'County'} ${cita.condado}` : ''}
            </div>
          )}
          {/* La razon, entera. Es lo que hace falta el dia de la cita. */}
          {!!cita.razon && (
            <div style={{ fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', marginTop: '.4rem', whiteSpace: 'pre-wrap',
              background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.45rem .6rem' }}>
              {cita.razon}
            </div>
          )}
          {!!cita.notas && (
            <div style={{ fontSize: '.75rem', color: 'var(--c-text2,#7aa3c0)', marginTop: '.35rem', whiteSpace: 'pre-wrap' }}>{cita.notas}</div>
          )}

          <div style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)', marginTop: '.45rem', fontFamily: 'var(--font-mono)' }}>
            ✍️ {es ? 'Escrito por' : 'Written by'} {cita.creadoPor || (es ? 'alguien de la agencia' : 'someone at the agency')} · {fmt(cita.createdAt)}
            {!!cita.confirmadoPor && (
              <> · {es ? 'confirmada por' : 'confirmed by'} {cita.confirmadoPor}{cita.confirmedAt ? ` · ${fmt(cita.confirmedAt)}` : ''}</>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '.35rem', flexShrink: 0 }}>
          {!!onEditar && (
            <button type="button" onClick={() => onEditar(cita)} className="dkt-btn-s" style={{ fontSize: '.68rem', padding: '.25rem .6rem' }}>
              {es ? 'Editar' : 'Edit'}
            </button>
          )}
          {!!onBorrar && (
            <button type="button" onClick={() => onBorrar(cita)}
              style={{ background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7,
                padding: '.25rem .6rem', cursor: 'pointer', fontSize: '.68rem', fontWeight: 700, color: 'var(--c-red,#ff3d6b)' }}>
              {es ? 'Quitar' : 'Remove'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * El formulario de una cita.
 *
 * `personas` y `medicos` son los de la ficha del cliente; en la lista global
 * llegan vacíos y entonces solo se puede EDITAR lo demás (ver `soloLectura`).
 */
export function FormularioDeCita({ valor, personas = [], medicos = [], lang = 'es', guardando, onCambiar, onGuardar, onCerrar, titulo }) {
  const es = lang !== 'en';
  const set = (k, v) => onCambiar({ ...valor, [k]: v });
  const etiqueta = (c) => (es ? ETIQUETAS[c]?.es : ETIQUETAS[c]?.en) || c;
  const pista = (c) => (es ? ETIQUETAS[c]?.pistaEs : ETIQUETAS[c]?.pistaEn) || '';

  // Los médicos de la persona elegida. Cambiar de persona no puede dejar
  // colgado el médico de otra: se limpia solo.
  const medicosDeLaPersona = useMemo(
    () => medicos.filter((m) => m.persona === valor.persona),
    [medicos, valor.persona],
  );
  //
  // SOLO cuando la persona CAMBIA, y solo donde hay lista de personas (Fabiola
  // Montano, 2026-10-19: "al editar una cita médica no se cargan los datos del
  // proveedor"). Antes corría también al ABRIR el formulario, y desde Tareas →
  // Citas médicas no llega la lista de médicos de la ficha: el médico enlazado
  // "no estaba" en una lista vacía, se borraba, salían los campos del
  // proveedor vacíos, y al guardar la cita perdía su médico.
  const personaAlAbrir = useRef(valor.persona);
  useEffect(() => {
    if (personas.length === 0 || valor.persona === personaAlAbrir.current) return;
    personaAlAbrir.current = valor.persona;
    if (valor.doctorId && valor.doctorId !== VALOR_SUELTO && !medicosDeLaPersona.some((m) => m.id === valor.doctorId)) {
      onCambiar({ ...valor, doctorId: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor.persona]);

  const conMedico = !!valor.doctorId && valor.doctorId !== VALOR_SUELTO;
  const campos = CAMPOS_DE_CITA.filter((c) => !(ETIQUETAS[c]?.soloSuelto && conMedico));

  // EL CORRECTOR DE DIRECCIONES de la ficha del cliente, también en la cita
  // (Lissette 2026-10-23). Solo pregunta cuando se TECLEA en dirección, ciudad
  // o ZIP; al elegir una propuesta se llenan calle, ciudad, estado, ZIP y
  // condado. El apartamento (dir2) no se toca.
  const [dirTecleada, setDirTecleada] = useState(false);
  const DISPARAN = new Set(['dir1', 'ciudad', 'zip']);
  const elegirDireccion = (s) => {
    setDirTecleada(false);
    onCambiar({
      ...valor, dir1: s.street, ciudad: s.city || valor.ciudad, estado: s.state || valor.estado,
      zip: s.zip || valor.zip, condado: s.county || valor.condado,
    });
  };

  return (
    <Modal title={titulo} onClose={onCerrar} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
        {personas.length > 0 && (
          <div style={{ gridColumn: 'span 2' }}>
            <Select label={es ? '¿De quién es esta cita?' : 'Whose appointment is this?'}
              value={valor.persona} onChange={(e) => set('persona', e.target.value)}>
              {personas.map((p) => <option key={p.key} value={p.key}>{p.nombre} — {p.parentesco}</option>)}
              {!personas.some((p) => p.key === valor.persona) && (
                <option value={valor.persona}>{es ? 'Persona que ya no está en la ficha' : 'Person no longer on the record'}</option>
              )}
            </Select>
          </div>
        )}

        {personas.length > 0 && (
          <div style={{ gridColumn: 'span 2' }}>
            <Select label={es ? 'Médico' : 'Doctor'} value={valor.doctorId || ''}
              onChange={(e) => set('doctorId', e.target.value)}>
              <option value="">{es ? '— Elige un médico de la ficha —' : '— Pick a doctor on file —'}</option>
              {medicosDeLaPersona.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}{m.especialidad ? ` — ${m.especialidad}` : ''}</option>
              ))}
              {/* Se puede escribir un proveedor suelto sin tener que darlo de
                  alta como medico: un laboratorio al que se va una vez no
                  merece una ficha de medico. */}
              <option value={VALOR_SUELTO}>{es ? '✏️ Otro proveedor (escribirlo aquí)' : '✏️ Other provider (type it here)'}</option>
            </Select>
            {!valor.doctorId && medicosDeLaPersona.length === 0 && (
              <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: 3 }}>
                {es
                  ? 'Esta persona no tiene médicos puestos todavía. Elige "Otro proveedor" y escríbelo, o añádelo arriba.'
                  : 'This person has no doctors on file yet. Pick "Other provider" and type it, or add one above.'}
              </div>
            )}
          </div>
        )}

        {/* Sin la lista de la casa —se edita desde Tareas → Citas médicas— la
            persona y el médico se CONSERVAN, pero no se pueden cambiar aquí.
            Se dice: un desplegable que falta sin explicación parece un fallo. */}
        {personas.length === 0 && (
          <div style={{ gridColumn: 'span 2', fontSize: '.7rem', color: 'var(--c-text3,#45688a)',
            background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)',
            borderRadius: 8, padding: '.45rem .6rem' }}>
            {es
              ? 'De quién es la cita y qué médico la atiende se cambian desde la ficha del cliente (pestaña Médicos y citas). Aquí se quedan como están.'
              : 'Whose appointment it is and which doctor sees them are changed from the client record (Doctors & visits tab). They stay as they are here.'}
            {/* El médico enlazado, a la vista: sin esto el formulario no decía
                quién atiende y parecía que se había perdido (Fabiola 2026-10-19). */}
            {conMedico && !!valor.proveedorDeLaCita?.nombre && (
              <div style={{ marginTop: '.35rem', color: 'var(--c-text,#c8dff0)', fontSize: '.74rem' }}>
                🩺 <strong>{valor.proveedorDeLaCita.nombre}</strong>
                {[valor.proveedorDeLaCita.especialidad, valor.proveedorDeLaCita.npi ? `NPI ${valor.proveedorDeLaCita.npi}` : '', valor.proveedorDeLaCita.telefono ? `☎ ${valor.proveedorDeLaCita.telefono}` : '']
                  .filter(Boolean).map((x) => ` · ${x}`).join('')}
              </div>
            )}
          </div>
        )}

        <div>
          <Input label={(es ? 'Fecha y hora' : 'Date and time') + ' *'} type="datetime-local"
            value={valor.startsAt || ''} onChange={(e) => set('startsAt', e.target.value)} />
        </div>
        <div>
          <Select label={es ? 'Estatus' : 'Status'} value={valor.status || 'pendiente'}
            onChange={(e) => set('status', e.target.value)}>
            {ESTATUS_DE_CITA.map((id) => (
              <option key={id} value={id}>{ESTATUS[id].icono} {etiquetaDeEstatus(id, lang)}</option>
            ))}
          </Select>
        </div>

        {campos.map((c) => {
          const meta = ETIQUETAS[c] || {};
          const estilo = meta.ancho === 2 ? { gridColumn: 'span 2' } : undefined;
          return (
            <div key={c} style={estilo}>
              {meta.area
                ? <Textarea label={etiqueta(c)} maxLength={LARGO_MAXIMO[c]} value={valor[c] || ''}
                    placeholder={pista(c)} onChange={(e) => set(c, e.target.value)} />
                : <Input label={etiqueta(c)} maxLength={LARGO_MAXIMO[c]} value={valor[c] || ''}
                    placeholder={pista(c)} onChange={(e) => { if (DISPARAN.has(c)) setDirTecleada(true); set(c, e.target.value); }} />}
              {c === 'dir1' && (
                <AddressSuggestions
                  query={{ street: valor.dir1 || '', city: valor.ciudad || '', state: valor.estado || '', zip: valor.zip || '' }}
                  enabled={dirTecleada} onPick={elegirDireccion} lang={lang} />
              )}
            </div>
          );
        })}

        {/* ── EL RECORDATORIO AL CLIENTE (fase 3) ──────────────────────────
            Lissette, 2026-09-16: *"la opción de recordatorio de 10 días antes y
            1 día antes, o los días que uno coloque, y que le llegue por correo"*.

            VACÍO de fábrica, y se ve que lo está: esto es lo único del módulo
            que sale del CRM hacia el cliente, y no puede salir sin que alguien
            lo pida en esta cita. */}
        <div style={{ gridColumn: 'span 2', marginTop: '.2rem' }}>
          <label className="dkt-input-lbl">
            📧 {es ? 'Recordarle al cliente por correo' : 'Remind the client by email'}
          </label>
          <div style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {DIAS_SUGERIDOS.map((d) => {
              const puesto = (valor.reminderDays || []).includes(d);
              return (
                <button key={d} type="button"
                  onClick={() => set('reminderDays', normalizarDias(
                    puesto ? (valor.reminderDays || []).filter((x) => x !== d) : [...(valor.reminderDays || []), d],
                  ))}
                  style={{ padding: '.26rem .6rem', borderRadius: 100, fontSize: '.7rem', fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'inherit', border: '1px solid',
                    background: puesto ? 'rgba(0,212,255,.12)' : 'transparent',
                    borderColor: puesto ? 'rgba(0,212,255,.35)' : 'var(--c-border,#1a3550)',
                    color: puesto ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)' }}>
                  {puesto ? '✓ ' : ''}{etiquetaDeAviso(d, lang)}
                </button>
              );
            })}
            {/* Los que no están entre los tres de un clic. Se pueden quitar. */}
            {(valor.reminderDays || []).filter((d) => !DIAS_SUGERIDOS.includes(d)).map((d) => (
              <button key={d} type="button"
                onClick={() => set('reminderDays', (valor.reminderDays || []).filter((x) => x !== d))}
                style={{ padding: '.26rem .6rem', borderRadius: 100, fontSize: '.7rem', fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit', border: '1px solid rgba(0,212,255,.35)',
                  background: 'rgba(0,212,255,.12)', color: 'var(--c-cyan,#00d4ff)' }}>
                ✓ {etiquetaDeAviso(d, lang)} ✕
              </button>
            ))}
            <input type="number" min="1" max={DIAS_MAXIMO} placeholder={es ? 'otros días…' : 'other days…'}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const n = Math.trunc(Number(e.currentTarget.value));
                if (!Number.isFinite(n) || n < 1 || n > DIAS_MAXIMO) return;
                set('reminderDays', normalizarDias([...(valor.reminderDays || []), n]));
                e.currentTarget.value = '';
              }}
              className="dkt-inp" style={{ width: 110, fontSize: '.72rem', padding: '.25rem .4rem' }} />
          </div>
          <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: 3 }}>
            {(valor.reminderDays || []).length
              ? (es
                ? `Le llegará un correo ${normalizarDias(valor.reminderDays).map((d) => etiquetaDeAviso(d, lang)).join(' y ')}, en su idioma. Sin la razón de la cita: un correo se reenvía y se queda en la bandeja.`
                : `They will get an email ${normalizarDias(valor.reminderDays).map((d) => etiquetaDeAviso(d, lang)).join(' and ')}, in their language. Without the reason: emails get forwarded and stay in the inbox.`)
              : (es
                ? 'Sin marcar nada, al cliente NO le llega ningún correo por esta cita.'
                : 'With nothing ticked, the client gets NO email about this appointment.')}
            {(valor.reminderDays || []).length >= MAXIMO_DE_AVISOS
              ? (es ? ` · Máximo ${MAXIMO_DE_AVISOS}.` : ` · Max ${MAXIMO_DE_AVISOS}.`)
              : ''}
          </div>
        </div>

        {/* CONFIRMACIÓN AL CLIENTE AL GUARDAR (Fabiola / Lissette 2026-10-23:
            *"no le llega la información por correo al momento que se carga la
            cita"*). Solo al crear, y marcada de entrada: se ve y se puede
            quitar. Mismo contenido que el recordatorio, sin la razón. */}
        {valor.esNueva && (
          <label style={{ gridColumn: 'span 2', display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer',
            fontSize: '.75rem', color: 'var(--c-text2,#7aa3c0)', marginTop: '.2rem' }}>
            <input type="checkbox" checked={!!valor.avisarAhora}
              onChange={(e) => set('avisarAhora', e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              ✉️ {es ? 'Enviar la confirmación al cliente ahora, por correo' : 'Email the confirmation to the client now'}
              <span style={{ display: 'block', fontSize: '.66rem', color: 'var(--c-text3,#45688a)' }}>
                {es
                  ? 'Fecha, hora, médico y dirección, en su idioma. Sin la razón de la cita.'
                  : 'Date, time, doctor and address, in their language. Without the reason.'}
              </span>
            </span>
          </label>
        )}

        {/* Apagada por defecto, y se dice por que: la cita es del CLIENTE con su
            medico, no una reunion del agente. */}
        <label style={{ gridColumn: 'span 2', display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer',
          fontSize: '.75rem', color: 'var(--c-text2,#7aa3c0)', marginTop: '.2rem' }}>
          <input type="checkbox" checked={!!valor.enElCalendario}
            onChange={(e) => set('enElCalendario', e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            🗓️ {es ? 'Ponerla también en mi calendario' : 'Also put it on my calendar'}
            <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)' }}>
              {es
                ? 'Solo si vas a acompañar o llamar ese día. Va sin la razón: un calendario se comparte y se sincroniza con Google.'
                : 'Only if you are going along or calling that day. The reason is left out: calendars get shared and synced to Google.'}
            </div>
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: '.5rem', marginTop: '.9rem' }}>
        <button type="button" onClick={onGuardar} disabled={guardando} className="dkt-btn-p" style={{ flex: 1, opacity: guardando ? 0.6 : 1 }}>
          {guardando ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}
        </button>
        <button type="button" onClick={onCerrar} className="dkt-btn-s" style={{ flex: 1 }}>
          {es ? 'Cancelar' : 'Cancel'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Todo lo que las dos pantallas hacen igual: guardar, borrar y cambiar el
 * estatus, con los mismos avisos. Lo que cambia entre ellas es COMO se pintan.
 */
export function useCitasMedicas({ clientId, lang = 'es', filtros }) {
  const es = lang !== 'en';
  const [citas, setCitas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [errorDeCarga, setErrorDeCarga] = useState('');
  const [hayMas, setHayMas] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cambiando, setCambiando] = useState({});

  const clave = JSON.stringify(filtros || {});
  const cargar = async () => {
    setCargando(true); setErrorDeCarga('');
    try {
      const r = await listMedicalAppointments({ ...(clientId ? { clientId } : {}), ...(filtros || {}) });
      setCitas(r.citas); setHayMas(r.hayMas);
    } catch (e) {
      setErrorDeCarga(e.message || (es ? 'No se pudo cargar' : 'Could not load'));
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId, clave]);

  const avisarRecorte = (recortados) => {
    if (!recortados?.length) return false;
    const nombres = recortados.map((c) => (es ? ETIQUETAS[c]?.es : ETIQUETAS[c]?.en) || c).join(', ');
    toast.error(es
      ? `Guardada, pero se acortó por ser muy largo: ${nombres}`
      : `Saved, but trimmed for length: ${nombres}`);
    return true;
  };

  const guardar = async (form, editando, clienteDeLaCita) => {
    if (guardando) return false;
    if (!form.startsAt) {
      toast.error(es ? 'Pon la fecha y la hora de la cita' : 'Set the date and time');
      return false;
    }
    setGuardando(true);
    try {
      const payload = {
        clientId: clienteDeLaCita || clientId,
        persona: form.persona,
        // `__suelto__` es solo de la pantalla: al servidor va sin médico.
        doctorId: form.doctorId && form.doctorId !== VALOR_SUELTO ? form.doctorId : null,
        // El `datetime-local` ya es hora LOCAL; `new Date(...)` la interpreta
        // como tal y el ISO que sale lleva su desfase. Nada de recomponerla a
        // mano: ahí es donde una cita de las 8 pm se va al día siguiente.
        startsAt: new Date(form.startsAt).toISOString(),
        status: form.status || 'pendiente',
        enElCalendario: !!form.enElCalendario,
        reminderDays: normalizarDias(form.reminderDays),
        // Confirmación al cliente al crear (Lissette 2026-10-23). Al editar, nunca.
        avisarAhora: !editando && !!form.avisarAhora,
      };
      for (const c of CAMPOS_DE_CITA) payload[c] = form[c] || '';
      const r = editando
        ? await updateMedicalAppointment(editando.id, payload)
        : await createMedicalAppointment(payload);
      await cargar();
      const avisoDeConfirmacion = textoDeLaConfirmacion(r.confirmacion, es);
      if (!avisarRecorte(r.recortados)) {
        if (avisoDeConfirmacion) toast.warn(avisoDeConfirmacion, { duration: 10000 });
        else toast.success(r.confirmacion?.enviado
          ? (es ? 'Cita guardada y confirmación enviada al cliente' : 'Appointment saved and confirmation sent to the client')
          : (es ? 'Cita guardada' : 'Appointment saved'));
      } else if (avisoDeConfirmacion) toast.warn(avisoDeConfirmacion, { duration: 10000 });
      // Se pidió el calendario y no se pudo: se dice, en vez de dejar creyendo
      // que la cita está en la agenda (regla de oro 83).
      if (payload.enElCalendario && r.cita && !r.cita.enElCalendario) {
        toast.error(es
          ? 'La cita se guardó, pero NO se pudo poner en tu calendario.'
          : 'The appointment was saved, but it could NOT be added to your calendar.');
      }
      return true;
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo guardar' : 'Save failed'));
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (cita) => {
    const ok = await confirmDialog({
      title: es ? '¿Quitar esta cita?' : 'Remove this appointment?',
      message: `${cuandoEs(cita.startsAt, lang)}${cita.proveedor?.nombre ? ` — ${cita.proveedor.nombre}` : ''}`
        + (es
          ? '. Se quita de la ficha y del calendario si estaba puesta ahí.'
          : '. It is removed from the record, and from the calendar if it was there.'),
      confirmLabel: es ? 'Quitar' : 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteMedicalAppointment(cita.id);
      await cargar();
      toast.success(es ? 'Cita quitada' : 'Appointment removed');
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo quitar' : 'Delete failed'));
    }
  };

  const cambiarEstatus = async (cita, status) => {
    if (status === cita.status) return;
    setCambiando((m) => ({ ...m, [cita.id]: true }));
    // Se pinta ya y se deshace si el servidor dice que no: marcar el estatus es
    // lo que más se hace en esta pantalla, y esperar el viaje en cada clic se
    // nota.
    setCitas((prev) => prev.map((x) => (x.id === cita.id ? { ...x, status } : x)));
    try {
      const nueva = await setMedicalAppointmentStatus(cita.id, status);
      if (nueva) setCitas((prev) => prev.map((x) => (x.id === cita.id ? { ...x, ...nueva } : x)));
    } catch (e) {
      setCitas((prev) => prev.map((x) => (x.id === cita.id ? { ...x, status: cita.status } : x)));
      toast.error(e.message || (es ? 'No se pudo cambiar el estatus' : 'Could not change the status'));
    } finally {
      setCambiando((m) => { const n = { ...m }; delete n[cita.id]; return n; });
    }
  };

  return { citas, cargando, errorDeCarga, hayMas, guardando, cambiando, cargar, guardar, borrar, cambiarEstatus };
}

export { VALOR_SUELTO, citaVacia, citaComoFormulario };
